/**
 * Payments / transactions list (admin-only API). The endpoint returns the
 * full set; filtering and pagination happen client-side in the provider.
 */

import { useMemo, useState } from 'react';
import { List, FunctionField, useGetList, useTranslate } from 'react-admin';
import { Chip } from '@mui/material';
import { CustomDatagrid, CrudListActions } from '@/components/common';
import { compactListSx } from '@/components/common/compactListSx';
import {
  CollapsibleFilterPanel,
  FilterExpandContext,
} from '@/components/common/CollapsibleFilterPanel';
import { CustomSelectInputFilter } from '@/components/common/CustomFilters';

const formatStoredDate = (value: unknown): string => {
  const iso = String(value ?? '');
  if (iso.length < 10) return '';
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
};

export const TransactionList = () => {
  const translate = useTranslate();
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const filterExpandValue = useMemo(
    () => ({ expanded: filtersExpanded, setExpanded: setFiltersExpanded }),
    [filtersExpanded]
  );

  const { data: clients } = useGetList('users', {
    filter: { role: 4 },
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'full_name', order: 'ASC' },
  });
  const { data: plans } = useGetList('plans', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'plan_id', order: 'ASC' },
  });

  const clientNames = useMemo(() => {
    const map: Record<number, string> = {};
    (clients ?? []).forEach((client: any) => {
      map[client.user_id] = client.company_name || client.full_name;
    });
    return map;
  }, [clients]);

  const planNames = useMemo(() => {
    const map: Record<number, string> = {};
    (plans ?? []).forEach((plan: any) => {
      map[plan.plan_id] = plan.name;
    });
    return map;
  }, [plans]);

  const filters = useMemo(
    () => [
      <CollapsibleFilterPanel key="filters" source="_cfp" alwaysOn alwaysVisibleCount={0}>
        <CustomSelectInputFilter
          source="payment_status"
          label={translate('resources.transactions.fields.payment_status')}
          choices={[
            { id: 1, name: translate('resources.transactions.status.paid') },
            { id: 0, name: translate('resources.transactions.status.pending') },
          ]}
        />
        <CustomSelectInputFilter
          source="user_id"
          label={translate('resources.transactions.fields.user_id')}
          choices={(clients ?? []).map((client: any) => ({
            id: client.user_id,
            name: client.company_name || client.full_name,
          }))}
        />
      </CollapsibleFilterPanel>,
    ],
    [translate, clients]
  );

  return (
    <FilterExpandContext.Provider value={filterExpandValue}>
      <List
        sx={compactListSx}
        filters={filters}
        actions={
          <CrudListActions
            showCreate={false}
            exportColumns={['created_on', 'user_id', 'plan_id', 'plan_price', 'plan_start_date', 'plan_end_date', 'payment_status', 'is_active']}
          />
        }
        sort={{ field: 'created_on', order: 'DESC' }}
        exporter={false}
      >
        <CustomDatagrid primarySource="transaction_id" bulkActionButtons={false}>
          <FunctionField
            source="created_on"
            render={(record: any) => formatStoredDate(record?.created_on)}
          />
          <FunctionField
            source="user_id"
            render={(record: any) => clientNames[record?.user_id] ?? record?.user_id ?? ''}
            sortable={false}
          />
          <FunctionField
            source="plan_id"
            render={(record: any) => planNames[record?.plan_id] ?? record?.plan_id ?? ''}
            sortable={false}
          />
          <FunctionField source="plan_price" render={(record: any) => `${record?.plan_price} €`} />
          <FunctionField
            source="plan_start_date"
            render={(record: any) => formatStoredDate(record?.plan_start_date)}
          />
          <FunctionField
            source="plan_end_date"
            render={(record: any) => formatStoredDate(record?.plan_end_date)}
          />
          <FunctionField
            source="payment_status"
            render={(record: any) => (
              <Chip
                size="small"
                color={record?.payment_status ? 'success' : 'warning'}
                label={translate(
                  `resources.transactions.status.${record?.payment_status ? 'paid' : 'pending'}`
                )}
              />
            )}
          />
        </CustomDatagrid>
      </List>
    </FilterExpandContext.Provider>
  );
};

export default TransactionList;
